import { useTranslation } from "react-i18next";
import { IBreadcrumbsAndMenu, ITab } from "../../../types/global";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import ContactImportUploader from "../../../components/Form/Contact/ImportUploader";
import BottomNavbar from "../../../components/UI/Navbar/BottomNavbar";
import { CustomizedBox } from "../../../components/Custom/CustomizedBox";
import CustomizedSteppers from "../../../components/Custom/CustomizedStepper";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import ContactImporterTable from "../../../components/Table/Contact/ImporterTable";
import CustomizedTab from "../../../components/Custom/CustomizedTab";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MouseEventHandler, useState } from "react";
import { ContactImportType } from "../../../generated/contact";
import useNormalContactImporter from "../../../hooks/Importer/Contact/use-normal-contact-importer";
import useContactChannelImporter from "../../../hooks/Importer/Contact/use-contact-channel-importer";
import { useContactPersonImporter } from "../../../hooks/Importer/Contact/use-contact-person-importer";
import { useContactAddressImporter } from "../../../hooks/Importer/Contact/use-contact-address-importer";
import { useContactAttributeImporter } from "../../../hooks/Importer/Contact/use-contact-attibute-importer";
import ImporterErrorModal from "../../../components/UI/Modal/ImporterErrorModal";
import { useModal } from "../../../hooks/use-modal";

const VendorImporter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("contact.index"),
      to: "/contact",
    },
    {
      name: t("contact.contact_type.vendor"),
      to: "/contact/vendor",
    },
    {
      name: `นำเข้า${t("contact.contact_type.vendor")}`,
    },
  ];
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isFilter = searchParams.get("filter");

  const methods = useForm<any>({
    defaultValues: {
      import_type: "vendor",
      type: "create",
      file: {},
      step: 0,
      errors: [],
    },
  });

  const steps = [
    t("inventory.items.import.upload_file"),
    t("inventory.items.import.validate_data"),
    t("inventory.items.import.import_data"),
  ];

  const tabs: ITab[] = [
    {
      label: t("status.success"),
      path: `${pathname}`,
    },
    {
      label: t("status.failed"),
      path: `${pathname}?filter=failed`,
    },
  ];

  const { watch, getValues, setValue } = methods;
  const activeStep = watch("step");
  const importType = watch("import_type");

  const currentTab = pathname + (isFilter ? `?filter=${isFilter}` : "");

  const [errorData, setErrorData] = useState<any[]>([]);

  const { modal, openModalHandler, closeModalHandler } = useModal();

  const { importHandler, isLoading, rowData, validateHandler } =
    useNormalContactImporter(
      ContactImportType.Vendor,
      getValues,
      setValue,
      setErrorData,
      openModalHandler
    );

  const {
    importHandler: importContactChannelHandler,
    isLoading: isContactChannelLoading,
    rowData: contactChannelData,
    validateHandler: validateContactChannel,
  } = useContactChannelImporter(
    ContactImportType.Vendor,
    getValues,
    setValue,
    setErrorData,
    openModalHandler
  );

  const {
    importHandler: importContactPersonHandler,
    isLoading: isContactPersonLoading,
    rowData: contactPersonData,
    validateHandler: validateContactPerson,
  } = useContactPersonImporter(
    ContactImportType.Vendor,
    getValues,
    setValue,
    setErrorData,
    openModalHandler
  );

  const {
    importHandler: importContactAddressHandler,
    isLoading: isContactAddressLoading,
    rowData: contactAddressData,
    validateHandler: validateAddress,
  } = useContactAddressImporter(
    ContactImportType.Vendor,
    getValues,
    setValue,
    setErrorData,
    openModalHandler
  );

  const {
    importHandler: importContactAttributeHandler,
    isLoading: isContactAttributeLoading,
    rowData: contactAttributeData,
    validateHandler: validateAttribute,
  } = useContactAttributeImporter(
    ContactImportType.Vendor,
    getValues,
    setValue,
    setErrorData,
    openModalHandler
  );

  const backToUploadHandler = () => {
    setValue("step", 0);
  };

  const renderTable = (importType: string) => {
    switch (importType) {
      case "vendor":
        if (isFilter) return <ContactImporterTable type="vendor" data={[]} />;
        return <ContactImporterTable type="vendor" data={rowData} />;
      case "vendor_contact_channel":
        if (isFilter) return <ContactImporterTable type="vendor" data={[]} />;
        return <ContactImporterTable type="vendor" data={contactChannelData} />;
      case "vendor_contact_person":
        if (isFilter) return <ContactImporterTable type="vendor" data={[]} />;
        return <ContactImporterTable type="vendor" data={contactPersonData} />;
      case "vendor_address":
        if (isFilter) return <ContactImporterTable type="vendor" data={[]} />;
        return <ContactImporterTable type="vendor" data={contactAddressData} />;
      case "vendor_attribute":
        if (isFilter) return <ContactImporterTable type="vendor" data={[]} />;
        return (
          <ContactImporterTable type="vendor" data={contactAttributeData} />
        );
    }
  };

  const renderContent = (step: number, importType: string) => {
    if (step === 0) {
      return <ContactImportUploader contactType="vendor" />;
    } else {
      return (
        <>
          {step === 3 && (
            <CustomizedTab tabs={tabs} currentTab={currentTab} divider table />
          )}
          {renderTable(importType)}
        </>
      );
    }
  };

  const renderButton = (step: number, importType: string) => {
    const getHandler = (
      handlerType: "validate" | "import"
    ): MouseEventHandler<HTMLButtonElement> | undefined => {
      switch (importType) {
        case "vendor":
          return handlerType === "validate" ? validateHandler : importHandler;
        case "vendor_contact_channel":
          return handlerType === "validate"
            ? validateContactChannel
            : importContactChannelHandler;
        case "vendor_contact_person":
          return handlerType === "validate"
            ? validateContactPerson
            : importContactPersonHandler;
        case "vendor_address":
          return handlerType === "validate"
            ? validateAddress
            : importContactAddressHandler;
        case "vendor_attribute":
          return handlerType === "validate"
            ? validateAttribute
            : importContactAttributeHandler;
        default:
          return undefined;
      }
    };

    const validateContactHandler = getHandler("validate");
    const importContactHandler = getHandler("import");

    switch (step) {
      case 0:
        return (
          <CustomizedButton
            title="ตรวจสอบไฟล์"
            variant="contained"
            onClick={validateContactHandler}
          />
        );
      case 1:
        return (
          <>
            <CustomizedButton
              title="ย้อนกลับ"
              variant="outlined"
              onClick={backToUploadHandler}
            />
            <CustomizedButton
              title="นำเข้าข้อมูล"
              variant="contained"
              onClick={importContactHandler}
            />
          </>
        );
      case 3:
        return (
          <CustomizedButton
            title="ดูผู้ขายทั้งหมด"
            variant="contained"
            onClick={() => navigate("/contact/vendor")}
          />
        );
      default:
        return null;
    }
  };

  if (
    isLoading ||
    isContactChannelLoading ||
    isContactPersonLoading ||
    isContactAddressLoading ||
    isContactAttributeLoading
  ) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Typography variant="h5" mt={3}>
        {`นำเข้า${t("contact.contact_type.vendor")}`}
      </Typography>
      <FormProvider {...methods}>
        <CustomizedBox>
          <Box my={5}>
            <CustomizedSteppers steps={steps} activeStep={activeStep} />
          </Box>
          {renderContent(activeStep, importType)}
        </CustomizedBox>
      </FormProvider>
      <BottomNavbar>
        <Stack direction={"row"} gap={1}>
          {renderButton(activeStep, importType)}
        </Stack>
      </BottomNavbar>
      <ImporterErrorModal
        open={modal}
        closeModalHandler={closeModalHandler}
        data={errorData}
      />
    </>
  );
};

export default VendorImporter;
